import styled, { useTheme } from 'styled-components';
import React from 'react';
import { useParams } from 'react-router-dom';

import { LiveEventDescription } from '@/pages/LiveEvent/LiveEventDescription';
import { JourneyEditHero } from '@/features/journey/admin/atoms/JourneyEditHero';
import { Layout } from '@/ui/Layout';
import { LiveEvent } from '@/pages/LiveEvent/store/liveEvent/types';
import { SaveToMyLearning } from '@/pages/user/MyLearning/components/SaveToMyLearning';
import { BackToJourney } from '@/pages/LiveEvent/BackToJourney';
import { Icon, IconPropType } from '@/ui/Icon';
import { useJourneyEnhancedNavigation } from '@/store/v2/journeys/useJourneyEnhancedNavigation';
import { JourneyUpNextLearning } from '@/features/journey/admin/atoms/JourneyUpNextLearning';
import { LiveEventControlButtons } from '@/pages/LiveEvent/LiveEventControlButtons';
import { LiveEventAttendanceStatus } from '@/pages/LiveEvent/LiveEventAttendanceStatus';
import { LiveEventHighlights } from '@/pages/LiveEvent/LiveEventHighlights';
import { useLiveEventLocations } from '@/pages/LiveEvent/LiveEventLocationsContext';

const BackToParentButtonContainer = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 16px;
  min-height: 30px;
`;

const MainContent = styled.div`
  gap: 48px;
  width: 100%;
  margin: 40px 0 64px;
  display: flex;
  justify-content: space-between;
`;

const LeftContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  align-items: baseline;
  width: 100%;
`;

const EndedEventNotification = styled.div`
  background: ${({ theme }) => theme.colors.neutral.container};
  color: ${(p) => p.theme.colors.neutral.onBackground};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  margin-bottom: 40px;
  border-radius: 4px;
  width: 100%;
`;

const EndedEventNotificationLabel = styled.div`
  ${({ theme }) => theme.typography.body2};
`;

const RightContentColumn = styled.div`
  width: 100%;
  display: flex;
  max-width: 424px;
  flex-direction: column;
`;

const RightContentColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const UpNextContainer = styled.div`
  width: 100%;
`;

export const LiveEventDetails = ({ id, image, title, teaser, progress, completedAt }: LiveEvent): JSX.Element => {
  const theme = useTheme();
  const { id: courseId } = useParams<{ id: string }>();

  const parsedCourseId = courseId ? parseInt(courseId) : NaN;

  const { nextItem, journeyId, allUpcomingItemsLocked, allUpcomingItemsUnlockAt } = useJourneyEnhancedNavigation({
    id: parsedCourseId,
  });

  const { activeLocation } = useLiveEventLocations();

  return (
    <Layout.ContentSection marginTop>
      <BackToParentButtonContainer>
        <BackToJourney />
        <LiveEventControlButtons id={id} title={title} />
      </BackToParentButtonContainer>
      <JourneyEditHero
        title={title}
        actions={
          <SaveToMyLearning
            isEvent
            item={{ entity: 'learning', id: Number(id) }}
            minimized
            saveNotificationLabel="Live event saved to My Learning"
            deleteNotificationLabel="Live event removed from My Learning"
          />
        }
        subtitle="Live event"
        withOverlay
        imageUrl={image}
      />
      <MainContent>
        <LeftContentColumn>
          {activeLocation?.hasEnded && (
            <>
              <EndedEventNotification>
                <Icon icon={'calendar-exclamation' as IconPropType} color={theme.colors.neutral.onContainer} />
                <EndedEventNotificationLabel>This event has ended.</EndedEventNotificationLabel>
              </EndedEventNotification>
              <LiveEventAttendanceStatus myEnrollment={activeLocation?.myEnrollment} />
            </>
          )}
          <LiveEventDescription id={id} title={title} teaser={teaser} progress={progress} completedAt={completedAt} />
        </LeftContentColumn>
        <RightContentColumn>
          <RightContentColumnContainer>
            {!activeLocation?.hasEnded && (
              <LiveEventHighlights title={title} id={id} teaser={teaser} parentJourneyId={journeyId} />
            )}
            {(!!nextItem || allUpcomingItemsLocked) && (
              <UpNextContainer>
                <JourneyUpNextLearning
                  learningCardProps={
                    nextItem
                      ? {
                          objectId: nextItem.contentId,
                          title: nextItem.content?.title,
                          imageUrl: nextItem.content?.imageURL,
                          type: nextItem.content?.metadata?.type,
                          level: nextItem.content?.metadata?.level,
                          totalEffort: nextItem.content?.plannedDuration,
                          variant: 'compact',
                        }
                      : undefined
                  }
                  isActionActive
                  actionHref={
                    nextItem?.content?.curations?.[0]?.launchURLPath ? `${nextItem.content.curations[0].launchURLPath}` : '#'
                  }
                  parentJourneyId={journeyId}
                  allUpcomingItemsLocked={allUpcomingItemsLocked}
                  unlockAt={allUpcomingItemsUnlockAt}
                />
              </UpNextContainer>
            )}
          </RightContentColumnContainer>
        </RightContentColumn>
      </MainContent>
    </Layout.ContentSection>
  );
};
